/**
 * Centralized PostHog event registry.
 * All frontend-only events are defined here.
 * Backend-tracked events should NOT be duplicated here.
 *
 * Usage:
 *   import { trackEvent, EVENTS } from '@/lib/posthog'
 *   trackEvent(EVENTS.PUBLIC_BOOKING_SERVICE_SELECTED, { business_slug, service_id })
 */
export const EVENTS = {
  // ── Public Booking (frontend-only, pre-submit steps) ──
  PUBLIC_BOOKING_SERVICE_SELECTED: 'public_booking_service_selected',
  PUBLIC_BOOKING_TIME_SELECTED: 'public_booking_time_selected',

  // ── Marketing acquisition (frontend-only CTA clicks) ──
  MARKETING_SIGNUP_CLICKED: 'marketing_signup_clicked',
  MARKETING_LOGIN_CLICKED: 'marketing_login_clicked',
  MARKETING_DEMO_CLICKED: 'marketing_demo_clicked',

  // ── Signup (frontend-only step) ──
  SIGNUP_STARTED: 'signup_started',
} as const

type EventName = (typeof EVENTS)[keyof typeof EVENTS]

const apiKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY
const host = import.meta.env.VITE_PUBLIC_POSTHOG_HOST

let posthogPromise: Promise<typeof import('posthog-js').default | null> | undefined

export function loadPostHog() {
  if (typeof window === 'undefined' || !apiKey) return Promise.resolve(null)

  posthogPromise ??= import('posthog-js').then(({ default: posthog }) => {
    if (!posthog.__loaded) {
      posthog.init(apiKey, {
        api_host: host || 'https://us.i.posthog.com',
        capture_pageview: false,
        capture_pageleave: true,
        autocapture: true,
      })
    }

    return posthog
  })

  return posthogPromise
}

export function trackEvent(event: EventName, properties?: Record<string, unknown>) {
  void loadPostHog().then((posthog) => posthog?.capture(event, properties))
}

export function resetPostHog() {
  void loadPostHog().then((posthog) => posthog?.reset())
}
